import React from 'react';
import { css } from '@emotion/react';
import { startCase } from 'lodash';
import { greys } from '@/styles/colors';
import { useClientUser, useOrderNotes } from '@/hooks';
import { FONT, Empty, Pill, Card, ACCENT, SOFT } from './detailTabBlocks';

// Redesigned Notes tab. Preserves every field the shared Note() renderer shows:
// author (with source_user_type handling), email, timestamp, note type, private /
// notified badges, and acknowledgement text — in a compact card-per-note layout.

const dateShort = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});

const timestamp = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
});

function authorLabel(note) {
    const type = note?.source_user_type;
    if (!type) return 'System';
    if (type === 'Admin') return 'Onward';
    return `(${type}) ${note?.user?.username || ''}`.trim();
}

export default function DetailTabNotes({ order }) {
    const { accountType } = useClientUser();
    const accountTypeCapital = startCase(accountType);
    const orderNotes = useOrderNotes([order], accountType);
    const notes = orderNotes?.all || [];

    if (notes.length === 0) {
        return <Empty top>No notes on this order.</Empty>;
    }

    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                gap: 10px;
                padding-top: 4px;
            `}
        >
            {notes.map((note, idx) => {
                const isPrivate = note?.private_to === accountTypeCapital;
                const showEmail = note?.source_user_type !== 'Admin' && note?.user?.email;
                const created = note?.created_at ? new Date(note.created_at) : null;

                return (
                    <Card key={note?.note_id || idx}>
                        <div
                            css={css`
                                display: flex;
                                align-items: flex-start;
                                gap: 8px;
                                margin-bottom: 6px;
                            `}
                        >
                            <div
                                css={css`
                                    min-width: 0;
                                `}
                            >
                                <div
                                    css={css`
                                        font-family: ${FONT};
                                        font-weight: 700;
                                        font-size: 12px;
                                        color: ${greys[3]};
                                        line-break: anywhere;
                                    `}
                                >
                                    {authorLabel(note)}
                                </div>
                                {showEmail ? (
                                    <div
                                        css={css`
                                            font-family: ${FONT};
                                            font-weight: 500;
                                            font-size: 11px;
                                            color: ${greys[1]};
                                            line-break: anywhere;
                                        `}
                                    >
                                        {note.user.email}
                                    </div>
                                ) : null}
                            </div>
                            {created ? (
                                <div
                                    css={css`
                                        margin-left: auto;
                                        text-align: right;
                                        flex: 0 0 auto;
                                        font-family: ${FONT};
                                        font-weight: 500;
                                        font-size: 10.5px;
                                        color: ${greys[1]};
                                        line-height: 1.35;
                                    `}
                                >
                                    <div>{dateShort.format(created)}</div>
                                    <div>{timestamp.format(created)}</div>
                                </div>
                            ) : null}
                        </div>

                        {isPrivate || note?.requires_acknowledgement || note?.type ? (
                            <div
                                css={css`
                                    display: flex;
                                    flex-wrap: wrap;
                                    gap: 6px;
                                    margin-bottom: 8px;
                                `}
                            >
                                {note?.type ? <Pill uppercase>{note.type}</Pill> : null}
                                {isPrivate ? (
                                    <Pill uppercase color={ACCENT.gold} background={SOFT.gold}>
                                        Private
                                    </Pill>
                                ) : null}
                                {note?.requires_acknowledgement ? (
                                    <Pill uppercase color={ACCENT.green} background={SOFT.green}>
                                        Onward/Shipper Notified
                                    </Pill>
                                ) : null}
                            </div>
                        ) : null}

                        <div
                            css={css`
                                font-family: ${FONT};
                                font-weight: 500;
                                font-size: 12.5px;
                                color: ${greys[2]};
                                line-height: 1.45;
                                line-break: anywhere;
                                ${note?.is_acknowledgement ? 'font-style: italic;' : ''}
                            `}
                        >
                            {note?.is_acknowledgement
                                ? `Acknowledged by ${note?.source_user_type}`
                                : note?.note}
                        </div>
                    </Card>
                );
            })}
        </div>
    );
}
